import React from 'react';
import type { monaco } from 'react-monaco-editor';
import { EditorProps } from '../EditorV3';

type UseEditorCompletionsProps = Pick<
  EditorProps,
  'completions' | 'wordBasedSuggestions' | 'language'
> & {
  monacoInstance?: typeof monaco;
};

const useEditorCompletions = ({
  monacoInstance,
  completions,
  language,
  wordBasedSuggestions,
}: UseEditorCompletionsProps) => {
  React.useEffect(() => {
    // wait until the instance ref exists
    if (!monacoInstance || !language) {
      return;
    }

    // avoid registering completion provider if nothing to provide
    if (completions.length === 0 && !wordBasedSuggestions) {
      return;
    }

    const registration = monacoInstance.languages.registerCompletionItemProvider(language, {
      provideCompletionItems(model, position) {
        const lineContent = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 0,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        });

        // Calculate the current indentation level by counting spaces at the beginning of the line
        const currentIndentation = lineContent?.match(/^\s*/)?.[0]?.length || 0;

        // Filter completions based on required indentation if specified
        const filteredCompletions = completions.filter(
          completion =>
            completion.requiredIndentation === undefined ||
            completion.requiredIndentation === currentIndentation,
        );

        const suggestions = filteredCompletions.map(completion => ({
          insertText: completion.value,
          label: completion.label || completion.value,
          kind: completion.kind || monacoInstance.languages.CompletionItemKind.Constant,
          detail: completion.detail || completion.type,
          documentation: completion.documentation,
        }));

        if (wordBasedSuggestions) {
          const wordModel = model.getWordAtPosition(position);
          const columnBeforeWord = wordModel?.word.length
            ? position.column - wordModel.word.length
            : 0;

          const textUntilPosition = model.getValueInRange({
            startLineNumber: 1,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: Math.abs(columnBeforeWord),
          });

          // @ts-ignore
          let tokens = [...textUntilPosition.matchAll(/\w+/gi)].map(x => x[0]);
          if (!tokens) {
            return {
              suggestions,
            } as monaco.languages.CompletionList;
          }
          const labels = suggestions.map(s => (s.label as string).toLowerCase());
          tokens = [...new Set(tokens.filter(w => labels.every(l => l !== w.toLowerCase())))];

          suggestions.push(
            ...tokens.map(word => ({
              kind: monacoInstance.languages.CompletionItemKind.Constant as number,
              label: word,
              insertText: word,
              detail: '',
              documentation: '',
            })),
          );
        }

        return { suggestions } as monaco.languages.CompletionList;
      },
    });

    // eslint-disable-next-line consistent-return
    return () => {
      registration.dispose();
    };
  }, [monacoInstance, completions, language, wordBasedSuggestions]);
};

export default useEditorCompletions;
